import { readLogyxError } from 'services/api/errors';

// The activation token Logyx core gives when the contact signs in, or signs up,
// on a company activation link (/v1/users/exists, /v1/users/register). The
// link's later steps (the companies to merge into, merge, register the company)
// send it as their bearer token: core acts as its user, never as a user the
// request names (logix-core docs/company-activation-and-api-tokens.md).
//
// Kept in sessionStorage, not in the URL: it survives a reload of this tab and
// stays out of the history, the logs and other sites' Referer headers. The
// module's copy covers a tab whose storage is blocked.
const STORAGE_KEY = 'companyActivationToken';

let current: string | null = null;

export const saveActivationToken = (token: unknown): void => {
  current = typeof token === 'string' && token ? token : null;
  try {
    if (current) {
      sessionStorage.setItem(STORAGE_KEY, current);
    } else {
      sessionStorage.removeItem(STORAGE_KEY);
    }
  } catch {
    // Blocked storage: the module's copy is used.
  }
};

export const clearActivationToken = (): void => saveActivationToken(null);

export const getActivationToken = (): string | null => {
  try {
    return sessionStorage.getItem(STORAGE_KEY) || current;
  } catch {
    return current;
  }
};

// A later step's 401: no activation token in this tab, or an expired one (it
// lasts an hour). The contact signs in on the link again.
export const isActivationSessionEnded = (error: unknown): boolean =>
  readLogyxError(error).status === 401;

// The headers of a link's later steps: none when the contact hasn't signed in
// on the link in this tab, and core then answers 401.
export const activationHeaders = (): Record<string, string> => {
  const token = getActivationToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
};
